import './contactForm.scss';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';

import {
    Button,
    Input,
    Select,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { CountryNameSelect } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Account/CountryNameSelect';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import arrowIconWhite from '@progymedia/gatsby-theme-boutik-ecommerce/src/images/icons/Icon-Fleche.svg';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import { validateEmail } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/validations';
import { ProvinceNameInput } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Account/ProvinceNameInput';
import DroletButton from '../../components/Primitives/DroletButton';

export const query = graphql`
    query {
        allProductsJson(filter: { category: { regex: "/Drolet/Heaters/" } }) {
            nodes {
                code
                category
                title {
                    fr_CA
                    en_CA
                    en_US
                }
            }
        }
        metadata: allProductsJson(
            filter: { code: { eq: "drolet_form-technical-support" } }
        ) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

const TechnicalSupport = ({ data }) => {
    const { t } = useTranslation('pages');
    const [type, setType] = useState(
        t('contact-forms.select-appliance-type') ?? ''
    );
    const [requestType, setRequestType] = useState(
        t('contact-forms.select-request-type') ?? ''
    );
    const [date, setDate] = useState('YYYY-MM-DD');
    const [model, setModel] = useState(t('contact-forms.select-model') ?? '');
    const [serialNumber, setSerialNumber] = useState('');
    const [question, setQuestion] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address, setAddress] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [countryName, setCountryName] = useState('');
    const [provinceName, setProvinceName] = useState('');
    const [isProvinceNameRequired, setIsProvinceNameRequired] = useState(false);
    const [serialNumbRequired, setSerialNumberRequired] = useState(true);

    const { navigateTranslated, currentLocale } = usePageLocalization();
    const metadataAttributes = data?.metadata?.nodes[0]?.attributes;
    const metadata = {
        title: metadataAttributes?.meta_title?.values[0][currentLocale],
        description:
            metadataAttributes?.meta_description?.values[0][currentLocale],
    };
    const [errors, setErrors] = useState({
        type: '',
        typeRequest: '',
        date: '',
        model: '',
        serialNumber: '',
        question: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        address: '',
        zipCode: '',
        city: '',
        countryName: '',
        provinceName: '',
    });

    useEffect(() => {
        if (!serialNumbRequired) {
            setErrors({
                ...errors,
                serialNumber: '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serialNumbRequired]); //I dont think I need "errors" here as a dependency because I only want to replace error->serialNumber if the states changes

    const { addAlert } = useContext(feedbackContext);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isFormValid = useCallback(() => {
        let isValid = true;

        for (const value of Object.values(errors)) {
            if (value !== '') {
                isValid = false;
                break;
            }
        }

        if (type.trim() === t('contact-forms.select-appliance-type')) {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                type: t('error.field-required'),
            }));
        }
        if (requestType.trim() === t('contact-forms.select-request-type')) {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                requestType: t('error.field-required'),
            }));
        }
        if (model.trim() === t('contact-forms.select-model')) {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                model: t('error.field-required'),
            }));
        }
        if (serialNumber.trim() === 'a') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                serialNumber: t('error.field-required'),
            }));
        }
        if (question.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                question: t('error.field-required'),
            }));
        }
        if (date.trim() === 'YYYY-MM-DD') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                date: t('error.field-required'),
            }));
        }
        if (firstName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                firstName: t('error.field-required'),
            }));
        }
        if (lastName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                lastName: t('error.field-required'),
            }));
        }
        if (email.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                email: t('error.field-required'),
            }));
        }
        if (phoneNumber.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                phoneNumber: t('error.field-required'),
            }));
        }
        if (address.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                address: t('error.field-required'),
            }));
        }
        if (zipCode.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                zipCode: t('error.field-required'),
            }));
        }
        if (city.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                city: t('error.field-required'),
            }));
        }
        if (countryName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                countryName: t('error.field-required'),
            }));
        }

        if (isProvinceNameRequired && provinceName.trim() === '') {
            isValid = false;
            setErrors((errors) => ({
                ...errors,
                provinceName: t('error.field-required'),
            }));
        }

        if (!isValid) {
            addAlert({
                message: t('toastMessages:invalid-form'),
                status: 'error',
            });
        }

        return isValid;
    }, [
        type,
        t,
        requestType,
        model,
        serialNumber,
        question,
        date,
        firstName,
        lastName,
        email,
        phoneNumber,
        address,
        zipCode,
        city,
        countryName,
        provinceName,
        isProvinceNameRequired,
        errors,
        addAlert,
    ]);

    const convertDate = () => {
        const dateArray = date.split('-');
        return dateArray[2] + '/' + dateArray[1] + '/' + dateArray[0];
    };

    const getApplianceType = (): string | undefined => {
        const found = applianceTypes.find(
            (applianceType) =>
                applianceType.type['fr_CA'] === type ||
                applianceType.type['en_US'] === type
        );
        return found ? found.type['en_CA'] : undefined;
    };

    const getTopic = (): string | undefined => {
        const found = typeOfRequest.find(
            (typeOfRequest) =>
                typeOfRequest.type['fr_CA'] === requestType ||
                typeOfRequest.type['en_US'] === requestType
        );
        return found ? found.type['en_CA'] : undefined;
    };

    const submitForm = useCallback(
        async (event) => {
            setIsLoading(true);
            event.preventDefault();
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    language: currentLocale == 'fr_CA' ? 'Français' : 'English',
                    origine: 'Drolet',
                    topic: getTopic(),
                    applianceType: getApplianceType(),
                    sku: model.split(' ')[0],
                    serialNumber: serialNumber,
                    plannedPurchase: convertDate(),
                    description: question,
                    firstname: firstName,
                    lastname: lastName,
                    telephone: phoneNumber,
                    email: email,
                    zipCode: zipCode,
                    street: address,
                    city: city,
                    state: provinceName,
                    country: countryName,
                }),
            };

            if (isFormValid()) {
                const response = await fetch(
                    '/inquiry/wtc-technical-support',
                    requestOptions
                );
                if (response.ok) {
                    navigateTranslated(`/contact/`);
                    addAlert({
                        message: t('toastMessages:contact-form-success'),
                        status: 'success',
                    });
                } else {
                    setIsLoading(false);
                    addAlert({
                        message: t('toastMessages:contact-form-failed'),
                        status: 'error',
                    });
                }
            } else {
                setIsLoading(false);
            }
        },
        [
            addAlert,
            address,
            city,
            countryName,
            provinceName,
            date,
            email,
            firstName,
            isFormValid,
            lastName,
            model,
            navigateTranslated,
            phoneNumber,
            question,
            requestType,
            serialNumber,
            t,
            type,
            zipCode,
        ]
    );

    const typeOfRequest = [
        {
            type: {
                fr_CA: 'L’installation',
                en_CA: 'Installation',
                en_US: 'Installation',
            },
        },
        {
            type: {
                fr_CA: 'L’utilisation',
                en_CA: 'Operation',
                en_US: 'Operation',
            },
        },
        {
            type: {
                fr_CA: 'La garantie',
                en_CA: 'Warranty',
                en_US: 'Warranty',
            },
        },
        {
            type: {
                fr_CA: 'L’entretien',
                en_CA: 'Maintenance',
                en_US: 'Maintenance',
            },
        },
        {
            type: {
                fr_CA: 'La performance',
                en_CA: 'Performance',
                en_US: 'Performance',
            },
        },
        {
            type: {
                fr_CA: "Produits d'entretien",
                en_CA: 'Care products',
                en_US: 'Care products',
            },
        },
    ];

    const applianceTypes = [
        {
            type: {
                fr_CA: 'Poêle à bois',
                en_CA: 'Wood Stove',
                en_US: 'Wood Stove',
            },
        },
        {
            type: {
                fr_CA: 'Encastrable à bois',
                en_CA: 'Wood insert',
                en_US: 'Wood insert',
            },
        },
        {
            type: {
                fr_CA: 'Foyer à bois',
                en_CA: 'Wood fireplace',
                en_US: 'Wood fireplace',
            },
        },
        {
            type: {
                fr_CA: 'Fournaise à bois',
                en_CA: 'Wood furnace',
                en_US: 'Wood furnace',
            },
        },
        {
            type: {
                fr_CA: 'Foyer extérieur',
                en_CA: 'Outdoor fireplace',
                en_US: 'Outdoor fireplace',
            },
        },
        {
            type: {
                fr_CA: 'Poêle à granules',
                en_CA: 'Pellet stove',
                en_US: 'Pellet stove',
            },
        },
        {
            type: {
                fr_CA: 'Fournaise à granules',
                en_CA: 'Pellet furnace',
                en_US: 'Pellet furnace',
            },
        },
        {
            type: {
                fr_CA: 'Autres',
                en_CA: 'Others',
                en_US: 'Others',
            },
        },
    ];

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <div className='container'>
                <div className='contactForm'>
                    <h1>{t('contact-forms.technical-form-title')}</h1>
                    <p>{t('contact-forms.contact-form-header1')}</p>
                    <p>{t('contact-forms.contact-form-header2')}</p>
                    <form
                        id='tech-support-form'
                        onSubmit={(event) => submitForm(event)}
                    >
                        <h3>{t('contact-forms.request-type')}</h3>
                        <div className='row' style={{ marginBottom: '1.3rem' }}>
                            <div className='group'>
                                <label>
                                    {t('contact-forms.request-type')}
                                    <span className='asterisk'>{' *'}</span>
                                </label>
                                <Select
                                    error={errors.type}
                                    className='infoDropdown dropdown'
                                    firstOption={t(
                                        'contact-forms.select-request-type'
                                    )}
                                    elements={typeOfRequest}
                                    displayAttribute='type'
                                    value={requestType}
                                    lang={currentLocale}
                                    key='type'
                                    selectType='product'
                                    onChange={(value) => {
                                        setRequestType(value);
                                        if (
                                            value ===
                                            t(
                                                'contact-forms.select-request-type'
                                            )
                                        )
                                            setErrors({
                                                ...errors,
                                                type: t('error.field-required'),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                type: '',
                                            });
                                    }}
                                />
                            </div>
                        </div>
                        <h3>{t('contact-forms.model-info')}</h3>
                        <div className='row'>
                            <div className='group'>
                                <label>
                                    {t('contact-forms.appliance-type')}
                                    <span className='asterisk'>{' *'}</span>
                                </label>
                                <Select
                                    error={errors.type}
                                    className='infoDropdown dropdown'
                                    firstOption={t(
                                        'contact-forms.select-appliance-type'
                                    )}
                                    elements={applianceTypes}
                                    displayAttribute='type'
                                    value={type}
                                    lang={currentLocale}
                                    key='type'
                                    selectType='product'
                                    onChange={(value) => {
                                        setType(value);
                                        if (
                                            value ===
                                            t(
                                                'contact-forms.select-appliance-type'
                                            )
                                        )
                                            setErrors({
                                                ...errors,
                                                type: t('error.field-required'),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                type: '',
                                            });
                                    }}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='group'>
                                <label>
                                    {t('contact-forms.model')}
                                    <span className='asterisk'>{' *'}</span>
                                </label>
                                <Select
                                    error={errors.model}
                                    className='infoDropdown dropdown'
                                    firstOption={t(
                                        'contact-forms.select-model'
                                    )}
                                    elements={data.allProductsJson.nodes}
                                    displayAttribute='title'
                                    value={model}
                                    lang={currentLocale}
                                    key='code'
                                    selectType='product'
                                    onChange={(value) => {
                                        setModel(value);

                                        if (
                                            value ===
                                            t('contact-forms.select-model')
                                        )
                                            setErrors({
                                                ...errors,
                                                model: t(
                                                    'error.field-required'
                                                ),
                                            });
                                        else {
                                            const appliance =
                                                data?.allProductsJson?.nodes?.find(
                                                    (e) =>
                                                        e?.code ===
                                                        value
                                                            ?.split('-')?.[0]
                                                            ?.trim()
                                                );
                                            if (
                                                appliance &&
                                                appliance?.category?.includes(
                                                    'Wood Inserts'
                                                )
                                            ) {
                                                setSerialNumberRequired(false);
                                            } else {
                                                setSerialNumberRequired(true);
                                            }
                                            setErrors({
                                                ...errors,
                                                model: '',
                                            });
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='group'>
                                <label>
                                    {t('contact-forms.serial-number')}
                                    {serialNumbRequired && (
                                        <span className='asterisk'>{' *'}</span>
                                    )}
                                </label>
                                <Input
                                    className='dropdown'
                                    error={errors.serialNumber}
                                    type='text'
                                    value={serialNumber}
                                    placeholder='#'
                                    onChange={(value) =>
                                        setSerialNumber(value.trim())
                                    }
                                    validateInput={() => {
                                        if (
                                            serialNumber === '' &&
                                            serialNumbRequired
                                        )
                                            setErrors({
                                                ...errors,
                                                serialNumber: t(
                                                    'error.field-required'
                                                ),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                serialNumber: '',
                                            });
                                        if (
                                            serialNumber &&
                                            serialNumber.startsWith('0')
                                        ) {
                                            setErrors((errors) => ({
                                                ...errors,
                                                serialNumber:
                                                    t('error.zero-serial'),
                                            }));
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <p className='serialInfo'>
                            {t('contact-forms.serial-number-help')}
                        </p>

                        <div className='row'>
                            <div className='group'>
                                <label>
                                    {t('contact-forms.purchase-date')}
                                    <span className='asterisk'>{' *'}</span>
                                </label>
                                <Input
                                    className='datePicker dropdown'
                                    error={errors.date}
                                    type='date'
                                    value={date}
                                    onChange={(value) => {
                                        setDate(value);
                                    }}
                                    validateInput={() => {
                                        if (date === 'YYYY-MM-DD')
                                            setErrors({
                                                ...errors,
                                                date: t('error.field-required'),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                date: '',
                                            });
                                    }}
                                />
                            </div>
                        </div>
                        <br />
                        <br />
                        <h3>
                            {t('contact-forms.question')}
                            <span className='asterisk'>{' *'}</span>
                        </h3>
                        <div className='row'>
                            <strong>
                                {t('contact-forms.question-details')}
                            </strong>
                        </div>
                        <div className='row'>
                            <div className='questionBox'>
                                <textarea
                                    className={
                                        errors.question
                                            ? 'commentsBox error'
                                            : 'commentsBox'
                                    }
                                    onChange={(event) => {
                                        setQuestion(event.target.value);

                                        if (question === '')
                                            setErrors({
                                                ...errors,
                                                question: t(
                                                    'error.field-required'
                                                ),
                                            });
                                        else
                                            setErrors({
                                                ...errors,
                                                question: '',
                                            });
                                    }}
                                />
                                <span
                                    className='inputError'
                                    style={{
                                        display: errors.question
                                            ? 'inline'
                                            : 'none',
                                    }}
                                >
                                    {errors.question}
                                </span>
                            </div>
                        </div>
                        <br />
                        <br />
                        <h3>
                            {t('contact-forms.contact-info-prompt')}
                            <span className='asterisk'>{' *'}</span>
                        </h3>
                        <div className='clientInfos'>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.firstname')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.firstName}
                                        value={firstName}
                                        onChange={(value) =>
                                            setFirstName(value)
                                        }
                                        validateInput={() => {
                                            if (firstName === '')
                                                setErrors({
                                                    ...errors,
                                                    firstName: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    firstName: '',
                                                });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.lastname')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.lastName}
                                        value={lastName}
                                        onChange={(value) => setLastName(value)}
                                        validateInput={() => {
                                            if (lastName === '')
                                                setErrors({
                                                    ...errors,
                                                    lastName: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    lastName: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.contact-email')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.email}
                                        value={email}
                                        onChange={(value) => setEmail(value)}
                                        validateInput={() => {
                                            if (email === '')
                                                setErrors({
                                                    ...errors,
                                                    email: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else if (!validateEmail(email))
                                                setErrors({
                                                    ...errors,
                                                    email: t(
                                                        'error.invalid-email'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    email: '',
                                                });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.phone')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='tel'
                                        value={phoneNumber}
                                        error={errors.phoneNumber}
                                        onChange={(value) =>
                                            setPhoneNumber(
                                                value.replace(/\D/g, '')
                                            )
                                        }
                                        validateInput={() => {
                                            if (phoneNumber === '')
                                                setErrors({
                                                    ...errors,
                                                    phoneNumber: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    phoneNumber: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row address'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.address')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.address}
                                        value={address}
                                        onChange={(value) => setAddress(value)}
                                        validateInput={() => {
                                            if (address === '')
                                                setErrors({
                                                    ...errors,
                                                    address: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    address: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.postcode')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        type='text'
                                        error={errors.zipCode}
                                        value={zipCode}
                                        onChange={(value) => setZipCode(value)}
                                        validateInput={() => {
                                            if (zipCode === '')
                                                setErrors({
                                                    ...errors,
                                                    zipCode: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    zipCode: '',
                                                });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.city')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>
                                    <Input
                                        value={city}
                                        error={errors.city}
                                        type='text'
                                        onChange={(value) => setCity(value)}
                                        validateInput={() => {
                                            if (city === '')
                                                setErrors({
                                                    ...errors,
                                                    city: t(
                                                        'error.field-required'
                                                    ),
                                                });
                                            else
                                                setErrors({
                                                    ...errors,
                                                    city: '',
                                                });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='group'>
                                    <label>
                                        {t('contact-forms.country')}
                                        <span className='asterisk'>{' *'}</span>
                                    </label>

                                    <CountryNameSelect
                                        countryName={countryName}
                                        onChange={(value: string) =>
                                            setCountryName(value)
                                        }
                                        firstOption={t(
                                            'contact-forms.country-select-placeholder'
                                        )}
                                        errorMessage={errors.countryName}
                                        validateValue={(value: string) => {
                                            setErrors({
                                                ...errors,
                                                countryName:
                                                    value === ''
                                                        ? t(
                                                              'error.field-required'
                                                          )
                                                        : '',
                                            });
                                        }}
                                    />
                                </div>

                                <div className='group'>
                                    <label>
                                        {t('contact-forms.province')}
                                        {isProvinceNameRequired && (
                                            <span className='asterisk'>
                                                {' *'}
                                            </span>
                                        )}
                                    </label>

                                    <ProvinceNameInput
                                        setIsRequired={
                                            setIsProvinceNameRequired
                                        }
                                        setProvinceName={setProvinceName}
                                        countryName={countryName}
                                        provinceName={provinceName}
                                        selectFirstOption={t(
                                            'contact-forms.province-select-placeholder'
                                        )}
                                        onChange={(value) =>
                                            setProvinceName(value)
                                        }
                                        errorMessage={errors.provinceName}
                                        validateValue={() => {
                                            setErrors({
                                                ...errors,
                                                provinceName:
                                                    provinceName === ''
                                                        ? t(
                                                              'error.field-required'
                                                          )
                                                        : '',
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='btnWrap'>
                            <DroletButton
                                isLoading={isLoading}
                                label={t('warranty.form.submit')}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};
export default TechnicalSupport;
